import { Box, createStyles } from "@mantine/core";
import { RoutePathsKeys } from "../../hooks/useNavigateRoute";
import { IconV3Props } from "@rpi/openapi-core";
import { tokens } from "@blurb/codex-react";
import React from "react";
import { useAuthUser } from "../../hooks/useAuthUser";
import { GraphQL } from "@rpi/openapi-api";
import { NavBarAccountButton } from "./NavBarAccountButton";
import { NavBarButton } from "./NavBarButton";
import { NavBarDevToolsButton } from "./NavBarDevToolsButton";

interface NavBarLink {
  name: string;
  route: RoutePathsKeys;
  icon: IconV3Props["icon"];
  requiredRole?: GraphQL.DashboardUserRole | GraphQL.DashboardUserRole[];
}

export const navCollapsedWidth = 80;

const useStyles = createStyles(() => ({
  navContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    height: "100%",
    padding: `${tokens.spacing[6]} 0 ${tokens.spacing[6]} ${tokens.spacing[4]}`,
    gap: tokens.spacing[0],
    backgroundColor: tokens.color.primitive.base.white,
    transition: "width .3s ease-in-out",
    position: "absolute",
    top: tokens.spacing[0],
    left: tokens.spacing[0],
    zIndex: 100,
    width: navCollapsedWidth,
    "&:hover, &:has(:focus-visible), &:has([aria-expanded='true'])": {
      width: 220,
      boxShadow: tokens.shadow.raised,
    },
  },
  navLogoImage: {
    alignItems: "center",
    justifyContent: "flex-start",
    flexShrink: 0,
    display: "block",
  },
  navScrollArea: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    flex: 1,
    minHeight: 0,
    overflowX: "hidden",
    overflowY: "auto",
    scrollbarWidth: "thin",
    scrollbarColor: `${tokens.color.primitive.light.charcoal[300]} transparent`,
    "&::-webkit-scrollbar": {
      width: tokens.spacing[1],
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: tokens.color.primitive.light.charcoal[300],
      borderRadius: tokens.radius.sm,
    },
  },
  navPrimaryMenu: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: tokens.spacing[4],
    alignItems: "flex-start",
    marginTop: tokens.spacing[10],
    paddingRight: tokens.spacing[4],
    flexShrink: 0,
    minHeight: tokens.spacing[0],
  },
  navSecondaryMenu: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    gap: tokens.spacing[4],
    alignItems: "flex-start",
    marginTop: "auto",
    paddingTop: tokens.spacing[4],
    paddingRight: tokens.spacing[4],
    borderTop: `1px solid ${tokens.color.primitive.light.charcoal[200]}`,
    flexShrink: 0,
  },
}));

const allNavLinks: NavBarLink[] = [
  {
    name: "Home",
    route: "home",
    icon: "home",
  },
  {
    name: "Products",
    route: "customer-products",
    icon: "book_5",
  },
  {
    name: "Orders",
    route: "orders",
    icon: "orders",
  },
  {
    name: "Payments",
    route: "billing",
    icon: "attach_money",
    requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.SuperAdmin],
  },
  {
    name: "Files",
    route: "print-assets",
    icon: "photo_prints",
  },
  {
    name: "Organizations",
    route: "customers",
    icon: "corporate_fare",
    requiredRole: GraphQL.DashboardUserRole.SuperAdmin,
  },
  {
    name: "Users",
    route: "dashboard-users",
    icon: "group",
    requiredRole: [GraphQL.DashboardUserRole.CustomerAdmin, GraphQL.DashboardUserRole.SuperAdmin],
  },
];

export const NavBar: React.FC = () => {
  const { classes } = useStyles();
  const user = useAuthUser();

  const links = allNavLinks.filter(({ requiredRole }) => {
    if (!requiredRole) return true;
    return Array.isArray(requiredRole) ? requiredRole.some((role) => user.hasRole(role)) : user.hasRole(requiredRole);
  });

  return (
    <Box component="nav" className={classes.navContainer} aria-label="Main navigation">
      <Box>
        <img src="/logo-rounded.svg" alt="Company logo" width={48} height={48} className={classes.navLogoImage} />
      </Box>

      <Box className={classes.navScrollArea}>
        <Box className={classes.navPrimaryMenu} aria-label="Primary navigation">
          {links.map((link) => (
            <NavBarButton key={link.route} name={link.name} route={link.route} icon={link.icon} />
          ))}
        </Box>
        <Box className={classes.navSecondaryMenu} aria-label="Secondary navigation">
          <NavBarDevToolsButton />
          <NavBarAccountButton />
        </Box>
      </Box>
    </Box>
  );
};
